import { createModal } from './modal.js';
import { save, load } from './persist.js';
import { postcodeResolves } from './address-lookup.js';
import { initCountrySelect } from './country-select.js';
import { formatAddress, formatAddressOneLine } from './address-format.js';

const FIELD_NAMES = ['firstName', 'lastName', 'addressLine1', 'addressLine2', 'addressLine3', 'city', 'postcode', 'country'];
const REQUIRED_FIELDS = ['firstName', 'lastName', 'addressLine1', 'city', 'postcode', 'country'];

const REQUIRED_MESSAGES = {
  firstName:    'First name is required',
  lastName:     'Last name is required',
  addressLine1: 'Address line 1 is required',
  city:         'City is required',
  postcode:     'Postcode is required',
  country:      'Country is required',
};

// Per-dialog instance registry, keyed by dialog.id. Lets other modules
// programmatically open a popup with pre-filled data via openAddressForm().
const instances = new Map();

export function initAddressForm() {
  document.querySelectorAll('[data-address-form]').forEach(wrap => {
    const instance = createInstance(wrap);
    const dialog   = wrap.querySelector('.addr-modal');
    if (dialog?.id) instances.set(dialog.id, instance);
  });
}

// Programmatically open a popup with pre-filled values, bypassing the
// data-saved-id load path. Used by the inline billing-address search to
// hand the picked Loqate address + page name fields into the popup.
export function openAddressForm(dialogId, prefill, opener) {
  const instance = instances.get(dialogId);
  instance?.openWithPrefill(prefill, opener);
}

function createInstance(wrap) {
    const display    = wrap.querySelector('.addr-display');
    const modal      = wrap.querySelector('.addr-modal');
    const form       = modal.querySelector('.addr-form');
    const confirmBtn = modal.querySelector('.modal__confirm');
    const errorEl    = modal.querySelector('.modal__error');
    const noticeEl   = modal.querySelector('.addr-form__notice');

    const titleEl         = modal.querySelector('.modal__title');
    const confirmDefault  = confirmBtn.textContent;
    const loadUrlTemplate = wrap.dataset.loadUrlTemplate || null;
    // Dialogs shared between "add a new address" and "edit an existing one"
    // (the address-editor opened by address-book.js) swap their title
    // between the two — other dialogs (billing) keep their static Pug title.
    const dynamicTitle    = wrap.hasAttribute('data-dynamic-title');
    const addTitle        = titleEl?.textContent || '';

    // Initialise the custom country dropdown. The option list is
    // pre-rendered inside the dialog markup (server-side / Pug); the
    // widget just wires up open/close + selection behaviour.
    const countryEl = form.querySelector('.addr-country');
    const countryWidget = countryEl ? initCountrySelect(countryEl) : null;

    let activeOpener = null;
    let isSaving     = false;
    let nextPrefill  = null;

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function fieldWrapperFor(name) {
      const input = form.elements[name];
      return input?.closest('.form-field') || null;
    }

    function setFieldError(name, msg) {
      const input   = form.elements[name];
      const wrapper = input?.closest('.form-field');
      if (!input || !wrapper) return;
      if (msg) {
        wrapper.classList.add('has-error');
        input.setAttribute('aria-invalid', 'true');
      } else {
        wrapper.classList.remove('has-error');
        input.setAttribute('aria-invalid', 'false');
      }
      const msgEl = wrapper.querySelector('.form-field__message');
      if (msgEl) msgEl.textContent = msg || '';
    }

    function clearAllFieldErrors() {
      FIELD_NAMES.forEach(name => setFieldError(name, ''));
    }

    function setSaving(saving) {
      isSaving = saving;
      modal.classList.toggle('is-saving', saving);
      confirmBtn.textContent = saving ? 'Saving…' : confirmDefault;
      confirmBtn.disabled    = saving;
    }

    function readAddress() {
      const data = {};
      FIELD_NAMES.forEach(name => {
        const input = form.elements[name];
        data[name] = input ? input.value.trim() : '';
      });
      return data;
    }

    function applyAddress(address) {
      FIELD_NAMES.forEach(name => {
        if (name === 'country') return;
        if (form.elements[name]) form.elements[name].value = address[name] || '';
      });
      // Country falls back to the default if the saved value isn't in the
      // allowed list — the widget can't represent a value it doesn't have
      // an option for.
      if (countryWidget) {
        const saved = (address.country || '').toUpperCase();
        countryWidget.setCountry(countryWidget.isAllowed(saved) ? saved : countryWidget.defaultCountry);
      }
    }

    function resetForm() {
      form.reset();
      FIELD_NAMES.forEach(name => {
        if (name === 'country') return;
        if (form.elements[name]) form.elements[name].value = '';
      });
      if (countryWidget) countryWidget.setCountry(countryWidget.defaultCountry);
      const search = form.querySelector('.addr-search');
      if (search) search.value = '';
    }

    // Validate the form on submit. Returns true if all rules pass; otherwise
    // decorates the failing fields with .has-error + a message and returns
    // false. Postcode is checked against Loqate Find — if the call fails
    // outright (no key / network error) we let it through and rely on the
    // server to reject if it's bad.
    async function validateClient() {
      const data = readAddress();
      let firstInvalid = null;

      REQUIRED_FIELDS.forEach(name => {
        if (!data[name]) {
          setFieldError(name, REQUIRED_MESSAGES[name]);
          if (!firstInvalid) firstInvalid = name;
        }
      });

      // Loqate postcode resolution only makes sense for UK postcodes.
      // For other countries we still require a non-empty postcode (handled
      // above by REQUIRED_FIELDS) but skip the format check entirely.
      if (data.postcode && data.country === 'GB' && !firstInvalid) {
        const resolves = await postcodeResolves(data.postcode);
        if (resolves === false) {
          setFieldError('postcode', 'Please enter a valid UK postcode');
          firstInvalid = 'postcode';
        }
      }

      if (firstInvalid) {
        form.elements[firstInvalid]?.focus();
        return false;
      }
      return true;
    }

    // Apply server-returned errors to the form. Shape:
    //   { errors: [{ field?: string, message: string }, ...] }
    // Items with a known field name decorate that field; items without (or
    // for unknown fields) accumulate into the dialog's modal__error block.
    function applyServerErrors(errors) {
      if (!Array.isArray(errors) || errors.length === 0) {
        showError('Could not save address. Please try again.');
        return;
      }
      const globals = [];
      let firstField = null;
      errors.forEach(({ field, message }) => {
        if (field && FIELD_NAMES.includes(field)) {
          setFieldError(field, message);
          if (!firstField) firstField = field;
        } else {
          globals.push(message);
        }
      });
      if (globals.length) showError(globals.join(' '));
      if (firstField) form.elements[firstField]?.focus();
    }

    async function primeForOpener(opener) {
      activeOpener = opener || null;
      clearError();
      clearAllFieldErrors();
      setSaving(false);
      resetForm();

      if (dynamicTitle && titleEl) {
        titleEl.textContent = opener?.dataset.savedId
          ? addTitle.replace(/^Add\b/, 'Edit')
          : addTitle;
      }

      // Editing a recipient's address bypasses the book and lands here
      // directly (see recipients.js) — the customer might not realise that
      // saving here updates their address book, so flag it explicitly.
      // Every other entry point (Add New Address, a book row's Edit, the
      // delivery-address flow) is already visibly "inside" the book, where
      // that's self-evident, so the notice stays hidden for those.
      if (noticeEl) noticeEl.hidden = !(opener && 'bookNotice' in opener.dataset);

      if (nextPrefill) {
        applyAddress(nextPrefill);
        nextPrefill = null;
      } else if (opener?.dataset.savedId) {
        const address = await load(opener.dataset.savedId, loadUrlTemplate);
        if (address) applyAddress(address);
      } else {
          const fn = opener?.dataset.defaultFirstName;
          const ln = opener?.dataset.defaultLastName;
          if (fn && form.elements.firstName) form.elements.firstName.value = fn;
          if (ln && form.elements.lastName) form.elements.lastName.value = ln;
      }

      requestAnimationFrame(() => form.elements[FIELD_NAMES[0]]?.focus());
    }

    const inline = wrap.hasAttribute('data-address-form-inline');

    let modalInstance = null;
    let close = () => {};
    if (!inline) {
      modalInstance = createModal(modal, {
        closeOnBackdrop: false,
        onBeforeOpen: primeForOpener,
        onClose: () => {
          activeOpener = null;
          nextPrefill  = null;
          clearError();
          clearAllFieldErrors();
          setSaving(false);
          if (noticeEl) noticeEl.hidden = true;
        },
      });
      close = modalInstance.close;
    }

    // Clear a field's error as soon as the user starts correcting it. Done
    // at form level so we only attach one listener.
    form.addEventListener('input', e => {
      const name = e.target?.name;
      if (name && FIELD_NAMES.includes(name)) setFieldError(name, '');
    });

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (isSaving) return;

      clearError();
      if (!(await validateClient())) return;

      const data    = readAddress();
      const saveUrl = activeOpener?.dataset.saveUrl;

      let saved;
      if (saveUrl) setSaving(true);
      try {
        saved = await save({
          saveUrl,
          payload:     data,
          savedId:     activeOpener?.dataset.savedId,
          dummyPrefix: 'dummy',
        });
      } catch (err) {
        console.error(err);
        if (saveUrl) setSaving(false);
        applyServerErrors(err?.errors);
        return;
      }
      if (saveUrl) setSaving(false);

      if (activeOpener) {
        activeOpener.dataset.savedId = saved.id;
        activeOpener.dispatchEvent(new CustomEvent('address-form:confirm', {
          bubbles: true,
          detail: {
            ...saved,
            formatted: formatAddress(saved),
            formattedOneLine: formatAddressOneLine(saved),
          },
        }));
      }
      if (display) display.textContent = formatAddress(saved);
      close();
    });

    return {
      open(opener)             { modalInstance?.open(opener); },
      close,
      destroy()                { modalInstance?.destroy(); },
      openWithPrefill(data, opener) {
        nextPrefill = data || null;
        modalInstance?.open(opener);
      },
    };
}
